import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import {
  CampaignOutlined as BroadcastIcon,
  Person as PersonIcon,
  Send as SendIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import {
  clearError,
  clearLastSentMessage,
  fetchSentMessages,
  sendBroadcastMessage,
  sendMessageToDriver
} from '../../redux/slices/driverMessagesSlice';
import brandingConfig from '../../config/brandingConfig';

dayjs.locale('de');

const DEFAULT_FORM = {
  title: 'Zentrale',
  message: '',
  priority: 'normal',
  recipientType: 'individual',
  selectedDriver: null,
  expirationHours: 2,
};

const DriverMessageComposerSheet = () => {
  const dispatch = useDispatch();
  const drivers = useSelector((state) => state.appData.drivers);
  const driversLoading = useSelector((state) => state.appData.isLoading.drivers);
  const { sendingMessage, error } = useSelector((state) => state.driverMessages);

  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [sentNotice, setSentNotice] = useState('');

  const driverOptions = useMemo(() => (
    drivers
      .filter(driver => driver.isOnline === true)
      .map(driver => ({
        id: driver.id,
        label: driver.name || 'Unbekannter Fahrer',
        phone: driver.phone || '',
      }))
  ), [drivers]);

  const expiresAt = useMemo(
    () => dayjs().add(formData.expirationHours, 'hour'),
    [formData.expirationHours]
  );

  const handleInputChange = (field, value) => {
    setSentNotice('');
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }

    if (error) {
      dispatch(clearError());
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = 'Titel ist erforderlich';
    } else if (formData.title.length > 50) {
      errors.title = 'Maximal 50 Zeichen';
    }

    if (!formData.message.trim()) {
      errors.message = 'Nachricht ist erforderlich';
    } else if (formData.message.length > 200) {
      errors.message = 'Maximal 200 Zeichen';
    }

    if (formData.recipientType === 'individual' && !formData.selectedDriver) {
      errors.selectedDriver = 'Fahrer auswählen';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetAfterSend = () => {
    setFormData(prev => ({
      ...DEFAULT_FORM,
      recipientType: prev.recipientType,
      selectedDriver: prev.recipientType === 'individual' ? prev.selectedDriver : null,
      expirationHours: prev.expirationHours,
    }));
    setFormErrors({});
  };

  const handleSendMessage = async () => {
    if (!validateForm()) return;

    const messageData = {
      title: formData.title.trim(),
      message: formData.message.trim(),
      priority: formData.priority,
      expiresAt: expiresAt.toDate()
    };

    try {
      if (formData.recipientType === 'broadcast') {
        await dispatch(sendBroadcastMessage(messageData)).unwrap();
        setSentNotice('Broadcast wurde gesendet.');
      } else {
        await dispatch(sendMessageToDriver({
          recipientId: formData.selectedDriver.id,
          ...messageData
        })).unwrap();
        setSentNotice(`Nachricht an ${formData.selectedDriver.label} wurde gesendet.`);
      }

      resetAfterSend();
      dispatch(clearLastSentMessage());
      dispatch(fetchSentMessages({ limitCount: 10 }));
    } catch (sendError) {
      console.error('Fehler beim Senden der Nachricht:', sendError);
    }
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {error && (
        <Alert severity="error" sx={{ py: 0.5 }}>
          {error}
        </Alert>
      )}

      {sentNotice && (
        <Alert severity="success" sx={{ py: 0.5 }}>
          {sentNotice}
        </Alert>
      )}

      <ToggleButtonGroup
        value={formData.recipientType}
        exclusive
        fullWidth
        size="small"
        onChange={(event, newValue) => {
          if (newValue) {
            handleInputChange('recipientType', newValue);
            if (newValue === 'broadcast') {
              handleInputChange('selectedDriver', null);
            }
          }
        }}
        aria-label="Empfängertyp"
      >
        <ToggleButton value="individual" aria-label="Einzelner Fahrer">
          <PersonIcon fontSize="small" sx={{ mr: 0.75 }} />
          Fahrer
        </ToggleButton>
        <ToggleButton value="broadcast" aria-label="Alle Fahrer">
          <BroadcastIcon fontSize="small" sx={{ mr: 0.75 }} />
          Alle
        </ToggleButton>
      </ToggleButtonGroup>

      {formData.recipientType === 'individual' && (
        <Autocomplete
          options={driverOptions}
          getOptionLabel={(option) => option.label}
          value={formData.selectedDriver}
          onChange={(event, newValue) => handleInputChange('selectedDriver', newValue)}
          loading={driversLoading}
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Fahrer"
              error={!!formErrors.selectedDriver}
              helperText={formErrors.selectedDriver}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {driversLoading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box>
                <Typography variant="body2">{option.label}</Typography>
                {option.phone && (
                  <Typography variant="caption" color="text.secondary">
                    {option.phone}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        />
      )}

      <TextField
        label="Titel"
        value={formData.title}
        onChange={(event) => handleInputChange('title', event.target.value)}
        error={!!formErrors.title}
        helperText={formErrors.title || `${formData.title.length}/50`}
        inputProps={{ maxLength: 50 }}
        size="small"
        fullWidth
      />

      <TextField
        label="Nachricht"
        value={formData.message}
        onChange={(event) => handleInputChange('message', event.target.value)}
        error={!!formErrors.message}
        helperText={formErrors.message || `${formData.message.length}/200`}
        inputProps={{ maxLength: 200 }}
        multiline
        minRows={2}
        maxRows={4}
        fullWidth
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
        <FormControl size="small">
          <Select
            value={formData.priority}
            onChange={(event) => handleInputChange('priority', event.target.value)}
            displayEmpty
          >
            <MenuItem value="normal">
              <Chip label="Normal" size="small" />
            </MenuItem>
            <MenuItem value="high">
              <Chip label="Wichtig" size="small" color="warning" />
            </MenuItem>
          </Select>
        </FormControl>

        <ToggleButtonGroup
          value={formData.expirationHours}
          exclusive
          size="small"
          onChange={(event, newValue) => {
            if (newValue !== null) {
              handleInputChange('expirationHours', newValue);
            }
          }}
          aria-label="Ablaufzeit"
        >
          <ToggleButton value={0.5} aria-label="30 Minuten">30m</ToggleButton>
          <ToggleButton value={2} aria-label="2 Stunden">2h</ToggleButton>
          <ToggleButton value={6} aria-label="6 Stunden">6h</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Typography variant="caption" color="text.secondary">
        Läuft ab: {expiresAt.format('DD.MM.YYYY HH:mm')}
      </Typography>

      <Button
        onClick={handleSendMessage}
        variant="contained"
        startIcon={sendingMessage ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
        disabled={sendingMessage}
        fullWidth
        sx={{
          backgroundColor: brandingConfig.colors.primary,
          '&:hover': {
            backgroundColor: brandingConfig.colors.primaryDark
          }
        }}
      >
        {sendingMessage ? 'Wird gesendet...' : 'Senden'}
      </Button>
    </Box>
  );
};

export default DriverMessageComposerSheet;
